import React, { useEffect, useRef, useState } from 'react';
import authApi, { ChatConversationSummary, ChatMessageResponse } from '../../../services/authApi';
import { toast } from 'react-toastify';
import '../tool-page.css';
import '../AdminTutorialsPage.css';
import './AdminChatTab.css';

const POLL_INTERVAL = 30000;

const AdminChatTab: React.FC = () => {
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [selected, setSelected]           = useState<ChatConversationSummary | null>(null);
  const [messages, setMessages]           = useState<ChatMessageResponse[]>([]);
  const [reply, setReply]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [loading, setLoading]             = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef   = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (selected) {
      loadMessages(selected.userId);
    }
  }, [selected?.userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const res = await authApi.adminGetConversations();
      setConversations(res.data ?? []);
    } catch { toast.error('Failed to load conversations'); }
    finally { setLoading(false); }
  };

  const loadMessages = async (userId: number) => {
    try {
      const res = await authApi.adminGetConversation(userId);
      setMessages(res.data ?? []);
      // refresh conversation list to clear unread badge
      loadConversations();
    } catch { toast.error('Failed to load messages'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    try {
      const res = await authApi.adminReply(selected.userId, reply.trim());
      setMessages((prev) => [...prev, res.data]);
      setReply('');
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const poll = async () => {
    try {
      const countRes = await authApi.adminGetUnreadCount();
      if ((countRes.data ?? 0) > 0) {
        await loadConversations();
        if (selected) loadMessages(selected.userId);
      }
    } catch {}
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const selectedStatus = selected ? getOnlineStatus(selected.lastActiveAt) : null;

  return (
    <div className="ach-shell">
      {/* Conversation list */}
      <div className="ach-sidebar">
        <div className="ach-sidebar-head">
          <p className="tool-eyebrow">Support</p>
          <h2 className="ach-sidebar-title">Inbox</h2>
        </div>
        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="adm-empty" style={{ padding: '24px 16px' }}>No messages yet.</div>
        ) : (
          <div className="ach-convo-list">
            {conversations.map((c) => {
              const status = getOnlineStatus(c.lastActiveAt);
              return (
                <button
                  key={c.userId}
                  className={`ach-convo-item ${selected?.userId === c.userId ? 'ach-convo-item--active' : ''}`}
                  onClick={() => setSelected(c)}
                >
                  <div className="ach-convo-avatar-wrap">
                    <div className="ach-convo-avatar">{c.userName.charAt(0).toUpperCase()}</div>
                    <span className={`ach-dot ${status.online ? 'ach-dot--online' : 'ach-dot--offline'}`} />
                  </div>
                  <div className="ach-convo-info">
                    <div className="ach-convo-name">
                      {c.userName}
                      {c.unreadCount > 0 && <span className="ach-unread-badge">{c.unreadCount}</span>}
                    </div>
                    <div className="ach-convo-last">{truncate(c.lastMessage, 42)}</div>
                    <div className="ach-convo-time">{formatTime(c.lastMessageAt)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Message panel */}
      <div className="ach-panel">
        {!selected || !selectedStatus ? (
          <div className="ach-empty-state">
            <span>💬</span>
            <p>Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="ach-panel-header">
              <div className="ach-convo-avatar-wrap">
                <div className="ach-panel-avatar">{selected.userName.charAt(0).toUpperCase()}</div>
                <span className={`ach-dot ach-dot--lg ${selectedStatus.online ? 'ach-dot--online' : 'ach-dot--offline'}`} />
              </div>
              <div>
                <div className="ach-panel-name">{selected.userName}</div>
                <div className="ach-panel-status">
                  {selectedStatus.online
                    ? <span className="ach-status-online">● Online</span>
                    : <>
                        <span className="ach-status-offline">{selectedStatus.label}</span>
                        {selectedStatus.detail && <span className="ach-status-detail"> · Last active: {selectedStatus.detail}</span>}
                      </>
                  }
                </div>
              </div>
            </div>

            <div className="ach-messages">
              {messages.map((m) => (
                <div key={m.id} className={`ach-msg ${m.senderType === 'ADMIN' ? 'ach-msg--admin' : 'ach-msg--user'}`}>
                  <div className="ach-bubble">{m.content}</div>
                  <div className="ach-time">{formatTime(m.createdAt)}</div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="ach-reply-row">
              <textarea
                className="ach-reply-input"
                placeholder="Type your reply… (Enter to send)"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={handleKey}
                rows={2}
              />
              <button className="adm-btn adm-btn-primary" onClick={sendReply} disabled={sending || !reply.trim()}>
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n) + '…' : s;
}
function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}
function getOnlineStatus(lastActiveAt: string | null): { online: boolean; label: string; detail: string | null } {
  if (!lastActiveAt) return { online: false, label: 'Never seen', detail: null };
  const last = new Date(lastActiveAt);
  const diffMs = Date.now() - last.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 2) return { online: true, label: 'Online', detail: null };
  const detail = last.toLocaleString([], {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
  if (diffMin < 60) return { online: false, label: `${diffMin} min ago`, detail };
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return { online: false, label: `${diffHr}h ago`, detail };
  return { online: false, label: formatTime(lastActiveAt), detail };
}

export default AdminChatTab;
